import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articleDetails, articleComments, relatedArticles, type ArticleDetail, type ArticleComment, type RelatedArticle } from '@/mocks/article';

function Breadcrumb({ categoryName, categorySlug, title }: { categoryName: string; categorySlug: string; title: string }) {
  return (
    <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap">
      <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
        首页
      </Link>
      <span>/</span>
      <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
        {categoryName}
      </Link>
      <span>/</span>
      <span className="text-foreground-700 line-clamp-1">{title}</span>
    </nav>
  );
}

function ArticleHeader({ article }: { article: ArticleDetail }) {
  return (
    <header className="mb-8">
      <Link
        to="/"
        className="inline-block text-xs px-3 py-1 rounded-full bg-primary-100 text-primary-700 font-medium hover:bg-primary-200 transition-colors mb-4 whitespace-nowrap cursor-pointer"
      >
        {article.category}
      </Link>
      <h1 className="text-2xl md:text-4xl font-heading text-foreground-900 leading-tight mb-4">
        {article.title}
      </h1>
      <div className="flex items-center gap-4 flex-wrap text-xs text-foreground-500 mb-6">
        <div className="flex items-center gap-2">
          <img
            src={article.authorAvatar}
            alt={article.authorName}
            className="w-8 h-8 rounded-full object-cover"
            loading="lazy"
          />
          <span className="text-foreground-700 font-medium">{article.authorName}</span>
        </div>
        <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
        <span>{article.date}</span>
        <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
        <span className="flex items-center gap-1">
          <i className="ri-eye-line"></i>
          {article.readCount > 10000 ? `${(article.readCount / 10000).toFixed(0)}万阅读` : `${article.readCount}阅读`}
        </span>
        <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
        <span className="flex items-center gap-1">
          <i className="ri-chat-3-line"></i>
          {article.commentCount > 10000 ? `${(article.commentCount / 10000).toFixed(0)}万评论` : `${article.commentCount}评论`}
        </span>
      </div>
      <div className="rounded-xl overflow-hidden bg-background-100">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-auto object-cover object-top"
          title={article.title}
        />
      </div>
    </header>
  );
}

function ArticleTags({ tags }: { tags: string[] }) {
  return (
    <div className="flex items-center gap-2 flex-wrap mt-8 pt-6 border-t border-background-200/70">
      {tags.map((tag) => (
        <span
          key={tag}
          className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          #{tag}
        </span>
      ))}
    </div>
  );
}

function CommentItem({ comment }: { comment: ArticleComment }) {
  return (
    <div className="flex gap-3 py-4 border-b border-background-200/70 last:border-b-0">
      <img
        src={comment.avatar}
        alt={comment.username}
        className="w-9 h-9 rounded-full object-cover flex-shrink-0"
        loading="lazy"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-sm font-medium text-foreground-900 whitespace-nowrap">{comment.username}</span>
          <span className="text-xs text-foreground-400">{comment.date}</span>
        </div>
        <p className="text-sm text-foreground-700 leading-relaxed mb-2">{comment.content}</p>
        <div className="flex items-center gap-4">
          <button className="flex items-center gap-1 text-xs text-foreground-400 hover:text-primary-500 transition-colors cursor-pointer">
            <i className="ri-thumb-up-line"></i>
            {comment.likes > 10000 ? `${(comment.likes / 10000).toFixed(1)}万` : comment.likes}
          </button>
          <button className="flex items-center gap-1 text-xs text-foreground-400 hover:text-primary-500 transition-colors cursor-pointer">
            <i className="ri-chat-1-line"></i>
            回复
          </button>
        </div>
      </div>
    </div>
  );
}

function CommentsSection({ comments }: { comments: ArticleComment[] }) {
  return (
    <section className="mt-10">
      <h3 className="text-lg font-semibold text-foreground-900 mb-4 flex items-center gap-2">
        <i className="ri-chat-3-line text-primary-500"></i>
        热门评论 ({comments.length})
      </h3>
      <div className="bg-background-50 rounded-lg border border-background-200/70 p-4 md:p-6">
        {comments.map((comment, index) => (
          <CommentItem key={index} comment={comment} />
        ))}
        {comments.length === 0 && (
          <p className="text-sm text-foreground-500 text-center py-6">暂无评论，来做第一个吃瓜群众吧！</p>
        )}
      </div>
    </section>
  );
}

function RelatedCard({ article }: { article: RelatedArticle }) {
  return (
    <Link
      to="/"
      className="group flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
    >
      <div className="w-24 h-16 flex-shrink-0 rounded-md overflow-hidden bg-background-100">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover object-top"
          loading="lazy"
        />
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-xs text-primary-600 font-medium whitespace-nowrap">{article.category}</span>
        <h4 className="text-sm font-medium text-foreground-800 leading-snug mt-1 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h4>
        <span className="text-xs text-foreground-400 mt-1 block">{article.date}</span>
      </div>
    </Link>
  );
}

function RelatedArticles({ articles }: { articles: RelatedArticle[] }) {
  return (
    <section className="mt-10">
      <h3 className="text-lg font-semibold text-foreground-900 mb-4 flex items-center gap-2">
        <i className="ri-links-line text-primary-500"></i>
        相关文章
      </h3>
      <div className="bg-background-50 rounded-lg border border-background-200/70 p-2 md:p-4">
        <div className="flex flex-col">
          {articles.map((article) => (
            <RelatedCard key={article.slug} article={article} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const fallbackArticle = Object.values(articleDetails)[0];
  const article = slug ? (articleDetails[slug] || fallbackArticle) : fallbackArticle;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="max-w-[1280px] mx-auto px-4 md:px-6 pt-24 min-h-[60vh] flex flex-col items-center justify-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4"></i>
          <h1 className="text-xl font-heading text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">您要看的文章可能已被删除或链接有误</p>
          <Link
            to="/"
            className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const comments = articleComments[article.slug] || [];
  const related = relatedArticles[article.slug] || relatedArticles.default || [];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    author: {
      '@type': 'Person',
      name: article.authorName,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '吃瓜51网',
      url: 'http://jsqcyl.cn/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://jsqcyl.cn/article/${article.slug}`,
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://jsqcyl.cn/' },
      { '@type': 'ListItem', position: 2, name: article.category, item: 'http://jsqcyl.cn/' },
      { '@type': 'ListItem', position: 3, name: article.title, item: `http://jsqcyl.cn/article/${article.slug}` },
    ],
  };

  return (
    <>
      <Navbar />
      <main>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }}
        />

        <article className="max-w-[960px] mx-auto px-4 md:px-6 pt-24 md:pt-28 pb-10">
          <Breadcrumb
            categoryName={article.category}
            categorySlug={article.categorySlug}
            title={article.title}
          />
          <ArticleHeader article={article} />

          <div
            className="prose-custom text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.contentHtml }}
          />

          <ArticleTags tags={article.tags} />
        </article>

        <div className="max-w-[960px] mx-auto px-4 md:px-6 pb-14 md:pb-20">
          <CommentsSection comments={comments} />
          <RelatedArticles articles={related} />
        </div>
      </main>
      <Footer />
    </>
  );
}